import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { createStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/create';
import { fetchStockAdjustmentDetail } from '#/api/imsMgmt/receipts/stockAdjustment/fetchDetail';
import { updateStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/update';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import StockAdjustmentLinesEditor from '#/modules/imsMgmt/receipts/stockAdjustment/components/StockAdjustmentLinesEditor';
import {
  getStockAdjustmentApiErrorMessage,
  isStockAdjustmentVersionConflict,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/apiError';
import { canEditStockAdjustment } from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import {
  buildStockAdjustmentWritePayload,
  mapStockAdjustmentDetailToFormValues,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/formSerialize';
import {
  STOCK_ADJUSTMENT_FIELD_LABELS,
  stockAdjustmentFormSchema,
} from '#/modules/imsMgmt/receipts/stockAdjustment/schemas/stockAdjustmentSchema';
import {
  stockAdjustmentDetailQueryKey,
  stockAdjustmentListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  adjustmentDate: '',
  sourceType: 'MANUAL',
  sourceId: '',
  issueReason: '',
  remark: '',
  lines: [],
};

const CreateEditStockAdjustmentPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: stockAdjustmentDetailQueryKey(id),
    queryFn: () => fetchStockAdjustmentDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createStockAdjustment,
    updateFn: updateStockAdjustment,
    buildPayload: (values) => buildStockAdjustmentWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: stockAdjustmentListQueryKeyPrefix,
    detailQueryKey: stockAdjustmentDetailQueryKey,
    getEditPath: ROUTES.stockAdjustmentEdit,
    getErrorMessage: (error) => getStockAdjustmentApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isStockAdjustmentVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(stockAdjustmentFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapStockAdjustmentDetailToFormValues(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const detail = detailQuery.data?.data;
  const pageTitle = isEdit ? '編輯調整單' : '新增調整單';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditStockAdjustment(detail.status);

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={id ? ROUTES.stockAdjustmentDetail(id) : ROUTES.stockAdjustment}>
                {id ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getStockAdjustmentApiErrorMessage(detailQuery.error, '找不到調整單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <DocumentStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.stockAdjustmentDetail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <form
              className="sheet create_edit create_edit_form create_edit_form--page-scroll"
              onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
              noValidate
            >
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_ADJUSTMENT_FIELD_LABELS}
                customMessages={customMessages}
                className="mt-5"
              />

              <div className="create_edit_form__body divide-y divide-gray-500">
                <section className="pb-6">
                  <div className="form-block">
                    <h2 className="form-block__title">單據資訊</h2>
                  </div>
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-3',
                      '@4xl/sheet:grid-cols-2',
                    )}
                  >
                    <FloatingDateInput
                      id="adjustmentDate"
                      label="單據日"
                      containerClassName="col-span-1 @4xl/sheet:col-span-2"
                      control={control}
                      errors={errors}
                      placeholder="西元年/月/日"
                    />
                    <FloatingTextarea
                      id="issueReason"
                      label="開單原因"
                      register={register}
                      errors={errors}
                      rows={2}
                    />
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                      rows={2}
                    />
                  </div>
                </section>

                <StockAdjustmentLinesEditor
                  control={control}
                  register={register}
                  errors={errors}
                  getValues={getValues}
                />
              </div>

              <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={id ? ROUTES.stockAdjustmentDetail(id) : ROUTES.stockAdjustment}>
                    取消
                  </Link>
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? '儲存中…' : '儲存'}
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditStockAdjustmentPage;
